import { Component, inject, Injector, computed } from '@angular/core';
import {
  ControlContainer,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { FormFactoryFieldComponent } from 'ngx-dynamic-forms-factory';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { FieldErrorsComponent } from 'libs/ui/src/public-api';
import { TranslatePipe } from '@ngx-translate/core';
import { Validators } from '@angular/forms';

@Component({
  selector: 'app-input',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    PasswordModule,
    FieldErrorsComponent,
    TranslatePipe,
  ],
  template: `
    @if (field()) {
      <div>
        <span class="text-sm mb-1 block text-gray-800">
          {{ field().label | translate }}
          @if (showRequired()) {
            <span class="text-red-500">*</span>
          }
        </span>
        @if (field().description) {
          <p class="!text-xs mb-1 block text-gray-600">
            {{ field().description }}
          </p>
        }
        <div>
          <input
            class="w-full"
            pInputText
            [type]="field().type || 'text'"
            [formControlName]="field().options.formControlName"
            [placeholder]="
              field().placeholder ? (field().placeholder | translate) : ''
            "
            [pattern]="field().pattern"
          />
        </div>
        <app-field-errors [errors]="control?.errors" [control]="control" />
      </div>
    }
  `,
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () =>
        inject(ControlContainer, { optional: true, skipSelf: true }),
    },
  ],
})
export class InputComponent extends FormFactoryFieldComponent {
  private injector = inject(Injector);

  // Computed signal for efficient control access
  private controlSignal = computed<FormControl | null>(() => {
    const fieldValue = this.field();
    const controlName = fieldValue?.options?.formControlName;

    if (!controlName) {
      return null;
    }

    const container = this.injector.get(ControlContainer, null, {
      optional: true,
    });

    if (!container || !container.control) {
      return null;
    }

    const formGroup: any = container.control;
    return <FormControl>formGroup?.controls?.[controlName] ?? null;
  });

  // Override getter to use the computed signal
  override get control(): FormControl | null {
    return this.controlSignal();
  }

  showRequired() {
    return (
      this.field()?.options.validators &&
      this.field()?.options.validators.length > 0 &&
      this.field()?.options.validators.includes(Validators.required)
    );
  }
}
